import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TableSearchComponent } from '../../shared/table-search/table-search.component';
import { TableComponent } from '../../shared/table/table.component';
import { ButtonsComponent } from '../../shared/buttons/buttons.component';
import { MatTableDataSource } from '@angular/material/table';
import { RowData } from '../../shared/interfaces/table.data';
import { PanelNormalized, PanelTableData } from '../../interfaces/sv-test-pannel';
import * as SvTestConst from '../../shared/constants/sv-test-pannel';
import { SvTestPannelService } from '../../services/sv-test-pannel/sv-test-panel.service';
import { MatDialog } from '@angular/material/dialog';
import { SvPannelUploadModalComponent } from './sv-panel-upload-modal/sv-panel-upload-modal.component';
import { SvTestCreatePanelComponent } from './sv-test-create-panel/sv-test-create-panel.component';
import { ModalDialogComponent } from '../../shared/modal-dialog/modal-dialog.component';
import { MessageModalService } from '../../services/message-modal/message-modal.service';
import { environment } from '../../../environments/environment';
import { MultiFileUploadModalComponent } from '../../shared/multi-file-upload-modal/multi-file-upload-modal.component';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { SvtestEditModalComponent, SvtestEditModalData } from './edit-modal/svtest-edit-modal.component';
import { SvTestPanelInfoPanelComponent } from './info-panel/sv-test-panel-info-panel.component';
import { Subscription } from 'rxjs';
import { UploadService, UploadCompletionEvent } from '../../services/upload/upload.service';
import { UploadType } from '../../services/upload/upload-type';
import { MultiUploadModalData } from '../../shared/interfaces/modal.data';
import { SharePopupComponent } from '../../shared/share-popup/share-popup.component';

@Component({
  selector: 'app-sv-test-pannel',
  standalone: true,
  imports: [TableSearchComponent, TableComponent, ButtonsComponent, SvTestPanelInfoPanelComponent],
  templateUrl: './sv-test-panel.component.html',
  styleUrl: './sv-test-panel.component.css',
})
export class SvTestPannelComponent implements OnInit, OnDestroy {
  btnNameShare: string = $localize`Share`;
  btnName: string = $localize`Upload New Data`;
  btnMatStroked = 'mat-stroked-button';
  btnStyleStroked = 'btn-stroked';
  showActionColumn: boolean = true;
  displayedColumnLabels: string[] = [
    SvTestConst.Name,
    SvTestConst.Description,
    SvTestConst.Project,
    SvTestConst.Applications,
    SvTestConst.CreatedByUser,
    SvTestConst.CreatedOn,
    SvTestConst.Status,
  ];
  displayedColumns = SvTestConst.displayedColumns;
  dataSource: MatTableDataSource<RowData<PanelTableData>> = new MatTableDataSource<RowData<PanelTableData>>([]);
  showCheckboxInTable: boolean = true;
  btnStyleFilledSmallForPrimaryBtn = 'btn-filled-small';
  btnStyleFilledSmallForPrimaryBtnDisabled = 'btn-filled-small-disabled';
  btnMatFlat: string = 'mat-flat-button';
  selectedPanelItems: PanelTableData[] = [];

  isInfoPanelOpen = false;
  selectedPanel: PanelNormalized | null = null;
  private panelListData: PanelNormalized[] = [];

  /** Row with an open menu/panel/dialog; drives the "active" row highlight. */
  activeRowPanelId: number | null = null;

  readonly isRowActiveFn = (row: RowData<PanelTableData>): boolean =>
    this.activeRowPanelId != null && row.data.panelId === this.activeRowPanelId;

  folderName: string = '';
  uploadUrl = `${environment.apiBaseUrl}/api/v1/buckets/upload/panel`;
  private uploadCompletionSubscription!: Subscription;
  private destroy$ = new Subject<void>();
  @ViewChild(TableComponent)
  tableComponent!: TableComponent<PanelTableData>;

  constructor(
    public dialog: MatDialog,
    private svTestPannelService: SvTestPannelService,
    private messageModal: MessageModalService,
    private uploadService: UploadService
  ) {}

  ngOnInit() {
    this.loadPannelData();
    this.uploadCompletionSubscription = this.uploadService.uploadCompleted$.subscribe(
      (event: UploadCompletionEvent) => {
        if (event.uploadType === UploadType.Panel) {
          this.updateTableRowStatus(event.resourceId, event.finalStatus);
        }
      }
    );
  }

  private updateTableRowStatus(PanelID: string, status: 'completed' | 'error' | 'cancelled'): void {
    const PanelIDNumber = parseInt(PanelID, 10);
    if (isNaN(PanelIDNumber)) return;

    const rowIndex = this.dataSource.data.findIndex((row) => row.data.panelId === PanelIDNumber);

    if (rowIndex > -1) {
      const updatedData = [...this.dataSource.data];

      let newStatusString: PanelTableData['status'];
      if (status === 'completed') {
        newStatusString = 'uploaded';
      } else if (status === 'error') {
        newStatusString = 'error';
      } else if (status === 'cancelled') {
        newStatusString = 'canceled';
      } else {
        return;
      }

      updatedData[rowIndex].data.status = newStatusString;
      this.dataSource.data = updatedData;

      // Keep the normalized cache (read by the info panel) in sync with the live status.
      const panelIndex = this.panelListData.findIndex((panel) => panel.panelId === PanelIDNumber);
      if (panelIndex > -1) {
        this.panelListData[panelIndex].status = newStatusString;
      }
    }
  }

  loadPannelData() {
    this.svTestPannelService
      .getPannelList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (panels) => {
          this.setPanelListData(panels);
        },
        error: (err) => {
          this.messageModal.openModal($localize`Error`, $localize`Error while fetching list of panels.`);
          console.error('pannel list api failed:', err);
        },
      });
  }

  setPanelListData(panelList: PanelNormalized[]) {
    this.panelListData = panelList;
    const rowData = panelList.map((panelListItem) => {
      const applicationsList = (panelListItem.applications || []).map((app) => `${app.appName} (v${app.version})`);

      const groupsList = (panelListItem.groups || [])
        .sort((a, b) => (a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1))
        .map((group) => group.groupName);

      const rowDataItem = {
        ...panelListItem,
        name: panelListItem.name,
        description: panelListItem.description,
        userName: panelListItem.userName,
        createdOn: panelListItem.createdOn,
        status: panelListItem.status,
        applications: {
          list: applicationsList,
        },
        groupName: {
          list: groupsList,
        },
      };

      return {
        data: rowDataItem,
        selected: false,
      };
    });
    this.dataSource = new MatTableDataSource<RowData<PanelTableData>>(rowData);
    this.dataSource.filterPredicate = this.createFilterPredicate();
  }

  public createFilterPredicate(): (item: RowData<PanelTableData>, filter: string) => boolean {
    return (item: RowData<PanelTableData>, filter: string) => {
      const terms = filter.trim().toLowerCase().split(/\s+/); // split by space

      const dataValues = Object.entries(item.data)
        .filter(([, val]) => val != null)
        .map(([key, val]) => {
          if (typeof val === 'string' && val.includes('T') && !isNaN(Date.parse(val))) {
            // Assume it's an ISO date string -> extract date part
            return val.split('T')[0].toLowerCase();
          }
          return val.toString().toLowerCase();
        });

      // Each term must be found in at least one field
      return terms.every((term) => dataValues.some((value) => value.includes(term)));
    };
  }

  onFilterChanged(filterValue: string) {
    this.dataSource.filter = filterValue;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onRowMenuOpened(row: PanelTableData): void {
    this.activeRowPanelId = row.panelId;
  }

  onRowMenuClosed(): void {
    // Keep the highlight if a menu item opened the info panel or a dialog — that surface now owns it.
    if (!this.isInfoPanelOpen && this.dialog.openDialogs.length === 0) {
      this.activeRowPanelId = null;
    }
  }

  onInfoPanelClosed(): void {
    this.isInfoPanelOpen = false;
    this.activeRowPanelId = null;
  }

  openFileUploadModal1() {
    const dialogRef = this.dialog.open(SvPannelUploadModalComponent, {
      width: '50rem',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadPannelData();
      }
    });
  }

  openFileUploadModal(): void {
    this.selectedPanelItems = [];
    if (this.tableComponent) {
      this.tableComponent.resetSelections();
    }
    const dialogRef = this.dialog.open<MultiFileUploadModalComponent, MultiUploadModalData>(
      MultiFileUploadModalComponent,
      {
        width: '50rem',
        disableClose: true,
        data: {
          title: $localize`File Upload`,
          description: $localize`Select and upload the files of your choice`,
          primaryButtonLabel: $localize`Done`,
          secondaryButtonLabel: $localize`Cancel`,
          showPrimaryButton: true,
          showSecondaryButton: false,
          inputFileName: true,
          inputFileDescription: true,
          files: [],
          uploadConfig: {
            featureType: UploadType.Panel,
            acceptedFileTypes: '.txt, .csv, .zip',
            maxFileSize: 100000,
            useChunking: true,
            chunkSize: 10 * 1024 * 1024,
            apiUrl: this.uploadUrl,
            dictDefaultMessage: 'Drag panel files here or click to browse',
          },
        },
      }
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadPannelData();
      }
    });
  }

  onRunBtnClicked(data: PanelTableData) {
    this.selectedPanelItems = [];
    if (this.tableComponent) {
      this.tableComponent.resetSelections();
    }
    this.activeRowPanelId = data.panelId;
    this.svTestPannelService
      .getPanelById(String(data.panelId))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.openCreateModal(response.data);
        },
        error: (err) => {
          this.activeRowPanelId = null;
          console.error(err);
          this.messageModal.openModal($localize`Error`, $localize`Error while fetching the panel by ID.`);
        },
      });
  }

  openCreateModal(pannelData: any) {
    const dialogRef = this.dialog.open(SvTestCreatePanelComponent, {
      width: '50rem',
      data: {
        putativeFilePath: pannelData.FolderName,
        name: pannelData.Name,
        file: pannelData.file.file_name,
        panelId: pannelData.PanelID,
        inputType: pannelData.InputType,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.activeRowPanelId = null;
      if (result) {
        this.loadPannelData();
      }
    });
  }

  deletePanel(row: PanelTableData) {
    this.selectedPanelItems = [];
    if (this.tableComponent) {
      this.tableComponent.resetSelections();
    }
    this.activeRowPanelId = row.panelId;
    let dialogRef = this.dialog.open(ModalDialogComponent, {
      width: '50rem',
      data: {
        title: $localize`Delete SV Test Panel ?`,
        description: $localize`Once deleted, <b>${row.name}</b> Panel will no longer be available in the SV Test Panel list. `,
        showSecondaryButton: true,
        showPrimaryButton: true,
        secondaryButtonLabel: $localize`Cancel`,
        primaryButtonLabel: $localize`Yes, Delete`,
      },
    });
    dialogRef.afterClosed().subscribe(() => {
      this.activeRowPanelId = null;
    });

    const modalInstance = dialogRef.componentInstance;
    modalInstance.primaryAction.subscribe(() => {
      this.handleDeletePanel(modalInstance, row);
    });
  }

  handleDeletePanel(modalInstance: ModalDialogComponent, row: PanelTableData) {
    this.deletePanelItems(row).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.loadPannelData();
          modalInstance.closeModal(true);
        } else {
          modalInstance.showError($localize`${response.message}`);
        }
      },
      error: (err) => {
        console.error(err);
        modalInstance.showError($localize`Error while deleting the panel.`);
      },
    });
  }

  deletePanelItems(row: PanelTableData) {
    return this.svTestPannelService.panelDelete(row.panelId);
  }

  downloadPanel(row: PanelTableData) {
    this.selectedPanelItems = [];
    if (this.tableComponent) {
      this.tableComponent.resetSelections();
    }
    this.svTestPannelService
      .panelDownloadById(row.panelId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            const link = document.createElement('a');
            link.href = response.signed_url;
            link.click();
          } else {
            this.messageModal.openModal($localize`Error`, $localize`${response.message}`);
          }
        },
        error: (err) => {
          console.error(err);
          this.messageModal.openModal($localize`Error`, $localize`Error while downloading the panel by ID.`);
        },
      });
  }

  async handleSvTestEditClick(row: PanelTableData) {
    this.selectedPanelItems = [];
    if (this.tableComponent) {
      this.tableComponent.resetSelections();
    }
    this.activeRowPanelId = row.panelId;
    const dialogRef = this.dialog.open<SvtestEditModalComponent, SvtestEditModalData>(SvtestEditModalComponent, {
      width: `60rem`,
      data: {
        svTestData: row,
      },
    });
    const res = await lastValueFrom(dialogRef.afterClosed());
    this.activeRowPanelId = null;
    if (res && res.event === 'SUBMIT') {
      const panelData = res.data;
      this.svTestPannelService
        .editSvPanel(res.panelId, panelData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.messageModal.openModal($localize`Success`, $localize`Panel updated successfully.`);
            this.loadPannelData();
          },
          error: (err) => {
            console.error(err);
            this.messageModal.openModal($localize`Error`, $localize`Error while editing.`);
          },
        });
    } else if (res.event === 'RELOAD') {
      // Case B: Only groups changed -> Just refresh table
      this.messageModal.openModal($localize`Success`, $localize`Panel updated successfully.`);
      this.loadPannelData();
    }
  }

  handleGetInfoClick(event: PanelTableData) {
    this.selectedPanelItems = [];
    if (this.tableComponent) {
      this.tableComponent.resetSelections();
    }
    this.selectedPanel = this.panelListData.find((panel) => panel.panelId === event.panelId) ?? null;
    this.isInfoPanelOpen = true;
    this.activeRowPanelId = event.panelId;
  }

  downloadFile(signedUrl: string) {
    const a = document.createElement('a');
    a.href = signedUrl;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  handleDownloadClick(event: { type: 'results' | 'logs' | 'raw'; data: PanelTableData }) {
    this.selectedPanelItems = [];
    if (this.tableComponent) {
      this.tableComponent.resetSelections();
    }
    this.svTestPannelService
      .downloadPanel(event.data.panelId, event.type)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.downloadFile(response.signed_url);
        },
        error: (err) => {
          console.error(err);
          this.messageModal.openModal($localize`Error`, $localize`Error while downloading the Sample Result.`);
        },
      });
  }

  handleCheckboxChange(rowData: { data: PanelTableData; selected: boolean }) {
    if (rowData.selected) {
      const exists = this.selectedPanelItems.some((item) => item.panelId === rowData.data.panelId);
      if (!exists) {
        this.selectedPanelItems.push(rowData.data);
      }
    } else {
      this.selectedPanelItems = this.selectedPanelItems.filter((item) => item.panelId !== rowData.data.panelId);
    }
  }

  onShareClicked() {
    // Extract just the IDs from the selected items
    const selectedIds = this.selectedPanelItems.map((item) => item.panelId);

    // Open the dialog with the required data
    const dialogRef = this.dialog.open(SharePopupComponent, {
      width: '550px',
      data: {
        Ids: selectedIds,
        title: 'Panel',
        resource_path: 'panels',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.selectedPanelItems = [];
        if (this.tableComponent) {
          this.tableComponent.resetSelections();
        }
        this.loadPannelData();
      }
    });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
